import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';

// Get Google client ID from runtime config or process.env
const config = window.RUNTIME_CONFIG || {};
const GOOGLE_CLIENT_ID = config.REACT_APP_GOOGLE_CLIENT_ID || process.env.REACT_APP_GOOGLE_CLIENT_ID;

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in on page load
    const token = localStorage.getItem('auth_token');
    const userData = localStorage.getItem('user');
    const pendingStatus = localStorage.getItem('pending_approval');
    
    if (pendingStatus === 'true') {
      setPendingApproval(true);
      if (userData) {
        setUser(JSON.parse(userData));
      }
    } else if (token && userData) {
      setUser(JSON.parse(userData));
    }
    
    setLoading(false);
  }, []);

  const loginWithGoogle = async (tokenId) => {
    try {
      setLoading(true);
      setError(null);
      
      const requestData = { token: tokenId };
      const response = await api.post('/api/auth/google-login', requestData);
      
      // Handle pending approval
      if (response.data.pending_approval) {
        const { user: userData } = response.data;
        localStorage.setItem('pending_approval', 'true');
        localStorage.setItem('user', JSON.stringify(userData));
        
        setUser(userData);
        setPendingApproval(true);
        navigate('/pending-approval');
        return;
      }
      
      // Regular login for approved users
      const { access_token, user: userData } = response.data;
      
      // Store token and user data
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.removeItem('pending_approval');
      
      setUser(userData);
      setPendingApproval(false);
      navigate('/');
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Only @wishfin.com email addresses are allowed.');
      } else if (err.response) {
        setError('Login failed. Please try again.');
      } else {
        setError('Failed to login with Google. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('pending_approval');
    setUser(null);
    setPendingApproval(false);
    navigate('/login');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        pendingApproval,
        loginWithGoogle,
        logout,
        isAuthenticated: !!user && !pendingApproval,
        isAdmin: user?.is_admin === true,
        isReadOnly: user?.is_read_only === true
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
